import { Box, Typography } from '@mui/material';
import cx from 'classnames';
import React, { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getLoadedUsers } from 'store/user-service/selector';

import { MessageSender, NamespacesList } from 'core/enums/common';

import { CustomAvatar } from 'components/Header/CustomAvatar/CustomAvatar';

import { ReactComponent as EllogyAvatar } from 'assets/icons/ellogy-circle.svg';
import { titleTextColor } from 'assets/styles/_palette';

import styles from './Message.module.scss';
import { IMessage } from './types';

interface IMessageProps {
	model: IMessage;
}

export const Message: FC<IMessageProps> = memo(({ model }) => {
	const { t: chatMessagesTranslate } = useTranslation(NamespacesList['chatMessages']);

	const loadedUsers = useSelector(getLoadedUsers);

	const messageClasses = useMemo(
		() => (model.sender === 'user' ? styles['message__human'] : styles['message__bot']),
		[model.sender]
	);

	const messageWrapperInfo = useMemo(
		() =>
			model.sender === 'user'
				? styles['messageWrapper__info--human']
				: styles['messageWrapper__info--bot'],
		[model.sender]
	);

	const messageAuthor = useMemo(
		() => loadedUsers.find((user) => user.id === model.userId),
		[model, loadedUsers]
	);

	const formattedContent = useMemo(() => {
		const content =
			model.sender === MessageSender.System
				? chatMessagesTranslate(model.content)
				: model.content;
		if (!content) return '';
		// First handle escaped newlines (literal "\n" strings), then actual newline characters
		// Convert escaped newline strings to actual newlines, then convert all newlines to <br /> tags
		return content
			.replace(/\\n/g, '\n')
			.replace(/\n/g, '<br />');
	}, [model.content, model.sender, chatMessagesTranslate]);

	return (
		<Box
			className={cx(styles.messageWrapper, styles['messageWrapper__bot'])}
			sx={{ alignSelf: model.sender === 'user' ? 'flex-end' : 'flex-start' }}
		>
			{model.sender === MessageSender.chatGPT || model.sender === MessageSender.System ? (
				<EllogyAvatar />
			) : null}

			<Box className={cx(styles.messageWrapper__info, messageWrapperInfo)}>
				<Typography sx={{ fontWeight: '700', color: titleTextColor }}>
					{model.sender === MessageSender.chatGPT || model.sender === MessageSender.System
						? 'Ellogy'
						: `${messageAuthor?.firstName} ${messageAuthor?.lastName}`}
				</Typography>

				<Box
					className={cx(styles.message, messageClasses, {
						'rtl-able': model?.content?.match('[\u0600-\u06FF\u0750-\u077F -~]'),
					})}
					dangerouslySetInnerHTML={{
						__html: formattedContent,
					}}
				/>
			</Box>

			{!(model.sender === MessageSender.chatGPT || model.sender === MessageSender.System) ? (
				<CustomAvatar
					avatarLink={messageAuthor?.avatarLink}
					firstName={messageAuthor?.firstName}
					lastName={messageAuthor?.lastName}
				/>
			) : null}
		</Box>
	);
});
